import { computed } from 'vue';
import { useEndpoint, useToastNotifications } from '@apparatix/composables';
import { ApiResponseBase } from '@apparatix/types/Documents';
import type {
  CreateRenewalResult,
  ExpirationRow,
  ExpirationStatus,
  FilterExpirations,
  NotRenewingResult,
} from '@apparatix/types/Expirations';

export const getExpirationStatusSeverity = (status: ExpirationStatus): string => {
  switch (status) {
    case 'Expiring':
      return 'warn';
    case 'Expired':
      return 'danger';
    case 'Renewing':
      return 'info';
    case 'Renewed':
      return 'success';
    case 'Renewal Lost':
      return 'danger';
    default:
      return 'secondary';
  }
};

export const useExpirations = () => {
  const { showError, showSuccess, showInfo } = useToastNotifications();

  const filterEndpoint = useEndpoint<ApiResponseBase<FilterExpirations>>({
    url: '/crm/expirations/filter',
    type: 'POST',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const renewEndpoint = useEndpoint<ApiResponseBase<CreateRenewalResult>>({
    type: 'POST',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const notRenewEndpoint = useEndpoint<ApiResponseBase<NotRenewingResult>>({
    url: '/crm/expirations/not-renewing',
    type: 'POST',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const ignoreSubcontractEndpoint = useEndpoint<ApiResponseBase<{ status: string }>>({
    url: '/crm/expirations/ignore-subcontract',
    type: 'POST',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const notesEndpoint = useEndpoint<ApiResponseBase<{ notes: string, changed: boolean }>, { success: boolean, error?: string }>({
    type: 'PUT',
    successField: 'data',
    toast: { showOnSuccess: false },
    onError: errorData => showError(errorData?.error ?? 'Unable to save notes.'),
  });

  const isActing = computed(() =>
    renewEndpoint.isLoading.value
    || notRenewEndpoint.isLoading.value
    || ignoreSubcontractEndpoint.isLoading.value);

  const mutateRow = (row: ExpirationRow, changes: Partial<ExpirationRow>) => {
    Object.assign(row, changes);
  };

  const filterExpirations = async (status: 'Expiring' | 'All'): Promise<FilterExpirations | null> => {
    const response = await filterEndpoint.getResults({ filter: { status: status }});
    return response?.data ?? null;
  };

  const createRenewal = async (row: ExpirationRow): Promise<CreateRenewalResult | undefined> => {
    renewEndpoint.setEndpointUrl(`/crm/expirations/${row.contractId}/renew`);
    const response = await renewEndpoint.getResults({
      data: { endDate: row.endDate },
    });
    if (!response) return undefined;

    mutateRow(row, { status: 'Renewing', urgency: 'complete', opportunityId: response.data.opportunityId });
    showSuccess('Renewal opportunity created');
    if (response.data.note) {
      showInfo(response.data.note);
    }

    return response.data;
  };

  const setNotRenewing = async (row: ExpirationRow, reason: string): Promise<boolean> => {
    const response = await notRenewEndpoint.getResults({
      data: { contractId: row.contractId, reason: reason, endDate: row.endDate },
    });
    if (!response) return false;

    mutateRow(row, { status: 'Not Renewed', notes: response.data.notes, urgency: 'complete' });
    showSuccess('Contract marked as not renewing');
    if (response.data.note) {
      showInfo(response.data.note);
    }

    return true;
  };

  const ignoreSubcontract = async (row: ExpirationRow): Promise<boolean> => {
    const response = await ignoreSubcontractEndpoint.getResults({
      data: { contractId: row.contractId, endDate: row.endDate },
    });
    if (!response) return false;

    mutateRow(row, { status: 'Subcontract ignored', urgency: 'complete' });
    showSuccess('Subcontract flight ignored');

    return true;
  };

  const updateNotes = async (row: ExpirationRow, notes: string): Promise<boolean> => {
    notesEndpoint.setEndpointUrl(`/crm/expirations/${row.contractId}/notes`);
    const response = await notesEndpoint.getResults({ data: { notes: notes }});
    if (!response) return false;

    mutateRow(row, { notes: response.data.notes });

    return true;
  };

  return {
    createRenewal,
    filterExpirations,
    ignoreSubcontract,
    isActing,
    setNotRenewing,
    updateNotes,
  };
};
